import React, {useEffect} from 'react';
import {useParams} from "react-router-dom";
import {useQuery} from "react-query";
import Page from "components/Page";
import Card from "components/Card";
import DataImportProgressBar from "components/DataImportProgressBar";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import DotBadge, {Color as BadgeColour} from "components/DotBadge";
import {toTitleCase} from "services/string";
import ngage from "services/ngage";
import {ClockIcon} from "@heroicons/react/outline";

// Helpers ----

const statusColours: Record<string, BadgeColour> = {
  "pending": BadgeColour.YELLOW,
  "ongoing": BadgeColour.BRAND,
  "complete": BadgeColour.GREEN,
  "failed": BadgeColour.RED,
};

// Component ----

function ViewDataImport() {
  const {id} = useParams();

  const {data: dataImport, isLoading: dataImportIsLoading, refetch: refetchDataImport} = useQuery(
    ['imports', {id}],
    () => ngage.imports.get(Number(id)),
    {enabled: !!id, keepPreviousData: true}
  );

  useEffect(() => {
    if (!dataImport) return;

    let timeout: ReturnType<typeof setTimeout>;

    if (['pending', 'ongoing'].includes(dataImport.status)) {
      timeout = setTimeout(() => {
        void refetchDataImport();
      }, 2000)
    }

    return () => clearTimeout(timeout)
  }, [dataImport, refetchDataImport]);

  // Prepare page meta
  const pageMeta = [];

  if(dataImport){
    pageMeta.push({
      icon: ClockIcon,
      value: 'Submitted: ' + dataImport.created_at.toLocaleString({dateStyle: "medium", timeStyle: "medium"})
    })
  }

  return (
    <Page
      title={"Data Import"}
      meta={pageMeta}
      isLoading={dataImportIsLoading}
    >
      {!!dataImport && (
        <>
          <section className="mb-4">
            <Card>
              <DataImportProgressBar dataImport={dataImport}/>
            </Card>
          </section>

          <section>
            <Card heading={"Details"}>
              <DescriptionList>

                <DescriptionListItem span={2}>
                  <DescriptionTerm>Status</DescriptionTerm>
                  <DescriptionDetails>
                    <DotBadge text={toTitleCase(dataImport.status)} color={statusColours[dataImport.status]}/>
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Category</DescriptionTerm>
                  <DescriptionDetails>
                    {toTitleCase(dataImport.category)}
                  </DescriptionDetails>
                </DescriptionListItem>

                <DescriptionListItem span={1}>
                  <DescriptionTerm>Source</DescriptionTerm>
                  <DescriptionDetails>
                    {toTitleCase(dataImport.source)}
                  </DescriptionDetails>
                </DescriptionListItem>

                {!!dataImport.file_name && (
                  <DescriptionListItem span={2}>
                    <DescriptionTerm>File</DescriptionTerm>
                    <DescriptionDetails>
                      {dataImport.file_name}
                    </DescriptionDetails>
                  </DescriptionListItem>
                )}

                {!!dataImport.started_at && (
                  <>
                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Started At</DescriptionTerm>
                      <DescriptionDetails>
                        {dataImport.started_at.toLocaleString({dateStyle: "medium", timeStyle: "medium"})}
                      </DescriptionDetails>
                    </DescriptionListItem>

                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Ended At</DescriptionTerm>
                      <DescriptionDetails>
                        {dataImport.ended_at?.toLocaleString({dateStyle: "medium", timeStyle: "medium"}) ?? 'N/A'}
                      </DescriptionDetails>
                    </DescriptionListItem>

                    <DescriptionListItem span={1}>
                      <DescriptionTerm>Duration</DescriptionTerm>
                      <DescriptionDetails>
                        {dataImport.duration?.toHuman()}
                      </DescriptionDetails>
                    </DescriptionListItem>
                  </>
                )}
              </DescriptionList>
            </Card>
          </section>
        </>
      )}
    </Page>
  );
}

// Export ----

export default ViewDataImport;